/**
 * Relative time for activity lists: "Just now", "10 mins ago", "1 hour ago", "3 days ago".
 * Falls back to {@link formatActivityTimestamp} for events older than ~7 days.
 */
export function formatActivityRelativeTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);

  if (diffMs < 0) return 'Just now';
  if (sec < 60) return 'Just now';
  if (min < 60) return min === 1 ? '1 min ago' : `${min} mins ago`;
  if (hr < 24) return hr === 1 ? '1 hour ago' : `${hr} hours ago`;
  if (day < 7) return day === 1 ? '1 day ago' : `${day} days ago`;

  return formatActivityTimestamp(iso);
}

/** Absolute local date/time for `title` tooltips on relative labels. */
export function formatActivityAbsoluteTooltip(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

/** Display timestamps for activity feed: "Today at …", "Yesterday at …", or "Mar 21 at …". */
export function formatActivityTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const timeStr = d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = startOfDay(new Date());
  const eventDay = startOfDay(d);
  const diffDays = Math.round((today.getTime() - eventDay.getTime()) / 86400000);

  if (diffDays === 0) return `Today at ${timeStr}`;
  if (diffDays === 1) return `Yesterday at ${timeStr}`;

  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  return `${dateStr} at ${timeStr}`;
}

/** Turn `user_prompt_added` into a short label for the UI. */
export function formatActivityCategory(category: string): string {
  if (!category) return '';
  return category
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Short label for feed rows (sentence case, no “Recruiter” prefix noise). */
export function activityCategoryShortLabel(category: string): string {
  const map: Record<string, string> = {
    recruiter_viewed_profile: 'Profile view',
    recruiter_profile_dwell: 'Time on profile',
    recruiter_liked_section: 'Section like',
    recruiter_prompt_video_watch: 'Prompt video',
    recruiter_chat_request: 'Message request',
    recruiter_meeting_request: 'Interview request',
    recruiter_meeting_approved: 'Meeting confirmed',
    recruiter_meeting_denied: 'Meeting declined',
    recruiter_meeting_rescheduled: 'Reschedule proposed',
    recruiter_message_received: 'New message',
    recruiter_access_request: 'Access request',
    recruiter_access_approved: 'Access approved',
    recruiter_access_denied: 'Access denied',
    recruiter_saved_profile: 'Saved to list',
    recruiter_expanded_section: 'Section expanded',
    user_prompt_added: 'Prompt added',
    user_prompt_updated: 'Prompt updated',
    user_prompt_deleted: 'Prompt deleted',
    user_reference_added: 'Reference added',
    user_reference_confirmed: 'Reference confirmed',
    user_skills_updated: 'Skills updated',
    user_availability_added: 'Availability added',
    user_availability_removed: 'Availability removed',
    user_profile_updated: 'Profile updated',
    user_password_changed: 'Password changed',
    user_email_verified: 'Email verified',
    user_google_calendar_connected: 'Calendar connected',
    user_google_calendar_disconnected: 'Calendar disconnected',
    system_onboarding_complete: 'Onboarding',
    system_profile_activated: 'Profile live',
    system_profile_deactivated: 'Profile paused',
    user_pro_upgraded: 'Pro',
  };
  if (map[category]) return map[category];
  return formatActivityCategory(category);
}
